// src/components/HomeEventsSection.js
// "Upcoming events" section for the Home page — completely redesigned.
// Premium layout: full-bleed gradient background, glassmorphic featured card,
// clean list rows, live countdown ticks every second.
// Self-contained: fetches its own data and injects its own styles.
// Renders nothing if there are no upcoming events.
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import axios from "axios";
import BASE_URL from "../config";
import EventCountdown from "./EventCountdown";

export default function HomeEventsSection({ limit = 3 }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isRtl = lang === "ar";

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/events-public`, {
          params: { scope: "upcoming", limit },
          headers: { "Accept-Language": lang },
        });
        if (active) setEvents(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (active) setEvents([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [lang, limit]);

  if (loading) {
    return (
      <section className={`hes-section hes-loading-section ${isRtl ? "hes-rtl" : ""}`} aria-busy="true" aria-label={t("events.home_title", "Upcoming events")}>
        <style>{css}</style>
        <div className="container hes-container">
          <div className="hes-loading-header"><span /><span /></div>
          <div className="hes-loading-grid">
            <div className="hes-loading-feature" />
            <div className="hes-loading-list"><span /><span /><span /></div>
          </div>
        </div>
      </section>
    );
  }
  if (events.length === 0) return null;

  const featured = events[0];
  const rest = events.slice(1);

  const formatDate = (iso) => {
    if (!iso) return "";
    const locale = { en: "en-US", ar: "ar-DZ", fr: "fr-FR" }[lang] || "fr-FR";
    return new Date(iso).toLocaleDateString(locale, {
      year: "numeric", month: "long", day: "numeric",
    });
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    const locale = { en: "en-US", ar: "ar-DZ", fr: "fr-FR" }[lang] || "fr-FR";
    return new Date(iso).toLocaleTimeString(locale, {
      hour: "2-digit", minute: "2-digit",
    });
  };

  const regHref = (e) => (lang === "fr" ? e.register_url : `/${lang}${e.register_url}`);
  const allEventsHref = lang === "fr" ? "/evenements" : `/${lang}/evenements`;

  return (
    <section className={`hes-section ${isRtl ? "hes-rtl" : ""}`}>
      <style>{css}</style>

      {/* ── Decorative background blobs ── */}
      <div className="hes-bg">
        <div className="hes-blob hes-blob-1" />
        <div className="hes-blob hes-blob-2" />
      </div>

      <div className="container hes-container">

        {/* ══ SECTION HEADER ══════════════════════════════════════════ */}
        <div className="hes-header">
          <div className="hes-header-text">
            <span className="hes-eyebrow">
              <span className="hes-eyebrow-dot" />
              {t("events.home_kicker", "À ne pas manquer")}
            </span>
            <h2 className="hes-heading">
              {t("events.home_title", "Prochains événements")}
            </h2>
          </div>
          <Link to={allEventsHref} className="hes-viewall-btn">
            {t("home.see_More", "Voir plus")}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
        </div>

        {/* ══ MAIN GRID ═══════════════════════════════════════════════ */}
        <div className="hes-grid">

          {/* ── FEATURED (left) ───────────────────────────────────────── */}
          <div className="hes-featured-wrap">
            <div className="hes-featured-card">

              {/* Image / placeholder */}
              <div className="hes-feat-img-wrap">
                {featured.image ? (
                  <img
                    src={`${BASE_URL}${featured.image}`}
                    alt={featured.title}
                    className="hes-feat-img"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                ) : (
                  <div className="hes-feat-img-ph">
                    <i className="bi bi-calendar-event" />
                  </div>
                )}
                {/* gradient overlay */}
                <div className="hes-feat-overlay" />

                {/* Badges */}
                <div className="hes-feat-badges">
                  <span className={`hes-badge ${featured.status === "ongoing" ? "hes-badge-live" : "hes-badge-upcoming"}`}>
                    {featured.status === "ongoing"
                      ? <><span className="hes-live-pulse" />{t("events.ongoing", "En cours")}</>
                      : <><i className="bi bi-calendar-check" />{t("events.upcoming", "À venir")}</>
                    }
                  </span>
                  {featured.is_paid ? (
                    <span className="hes-badge hes-badge-paid">
                      <i className="bi bi-cash-coin" /> {featured.prix} DZD
                    </span>
                  ) : (
                    <span className="hes-badge hes-badge-free">
                      <i className="bi bi-gift" /> {t("events.free", "Gratuit")}
                    </span>
                  )}
                </div>

                {/* Title inside image */}
                <div className="hes-feat-title-wrap">
                  <h3 className="hes-feat-title">{featured.title}</h3>
                  <div className="hes-feat-meta">
                    {featured.lieu && (
                      <span>
                        <i className="bi bi-geo-alt-fill" />
                        {featured.lieu}
                      </span>
                    )}
                    <span>
                      <i className="bi bi-calendar3" />
                      {formatDate(featured.date_debut)}
                    </span>
                    <span>
                      <i className="bi bi-clock" />
                      {formatTime(featured.date_debut)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Bottom panel */}
              <div className="hes-feat-bottom">
                <div className="hes-countdown-label">
                  <i className="bi bi-hourglass-split" />
                  {featured.status === "ongoing"
                    ? t("events.happening_now", "C'est en cours")
                    : t("events.starts_in", "Commence dans")}
                </div>
                <EventCountdown
                  date={featured.date_debut}
                  dateEnd={featured.date_fin}
                  size="lg"
                />
                <Link to={regHref(featured)} className="hes-register-btn">
                  <i className="bi bi-pencil-square" />
                  {t("events.register", "S'inscrire")}
                  <i className={`bi bi-arrow-${isRtl ? "left" : "right"} hes-btn-arrow`} />
                </Link>
              </div>
            </div>
          </div>

          {/* ── SIDE LIST (right) ──────────────────────────────────────── */}
          <div className="hes-side">
            {rest.length === 0 ? (
              <div className="hes-empty">
                <div className="hes-empty-icon"><i className="bi bi-stars" /></div>
                <p>{t("events.more_soon", "D'autres événements bientôt.")}</p>
                <Link to={allEventsHref} className="hes-register-btn hes-register-btn-sm">
                  {t("events.see_all", "Voir tous les événements")}
                </Link>
              </div>
            ) : (
              <div className="hes-list">
                {rest.map((e, idx) => (
                  <Link key={e.id} to={regHref(e)} className="hes-event-row">
                    {/* Index number */}
                    <div className="hes-row-num">
                      {String(idx + 2).padStart(2, "0")}
                    </div>

                    {/* Thumbnail */}
                    <div className="hes-row-thumb">
                      {e.image ? (
                        <img
                          src={`${BASE_URL}${e.image}`}
                          alt={e.title}
                          onError={(ev) => { ev.target.style.display = "none"; }}
                        />
                      ) : (
                        <div className="hes-row-thumb-ph">
                          <i className="bi bi-calendar-event" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="hes-row-content">
                      <h4 className="hes-row-title">{e.title}</h4>
                      <div className="hes-row-info">
                        <span>
                          <i className="bi bi-calendar3" />
                          {formatDate(e.date_debut)}
                        </span>
                        {e.lieu && (
                          <span>
                            <i className="bi bi-geo-alt" />
                            {e.lieu}
                          </span>
                        )}
                      </div>
                      <EventCountdown date={e.date_debut} dateEnd={e.date_fin} size="sm" />
                    </div>

                    {/* Arrow */}
                    <div className="hes-row-cta">
                      <i className={`bi bi-arrow-${isRtl ? "left" : "right"}`} />
                    </div>
                  </Link>
                ))}

                {/* "See all" at bottom of list */}
                <Link to={allEventsHref} className="hes-seeall-row">
                  <i className="bi bi-grid-3x3-gap" />
                  {t("events.see_all", "Voir tous les événements")}
                  <i className={`bi bi-arrow-${isRtl ? "left" : "right"}`} />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   INJECTED CSS
───────────────────────────────────────────────────────────── */
const css = `
/* ── Instant loading state ─────────────────────────────── */
.hes-loading-section { min-height: 390px; }
.hes-loading-header { display: flex; justify-content: space-between; align-items: center; gap: 20px; margin-bottom: 30px; }
.hes-loading-header span, .hes-loading-feature, .hes-loading-list span {
  position: relative; overflow: hidden; display: block; border-radius: 14px;
  background: rgba(255,255,255,.1);
}
.hes-loading-header span::after, .hes-loading-feature::after, .hes-loading-list span::after {
  content: ''; position: absolute; inset: 0; transform: translateX(-100%);
  background: linear-gradient(100deg, transparent 15%, rgba(255,255,255,.3) 50%, transparent 85%);
  animation: hesLoadingShimmer 1.35s ease-in-out infinite;
}
.hes-loading-header span:first-child { width: 210px; height: 28px; }
.hes-loading-header span:last-child { width: 110px; height: 42px; border-radius: 99px; }
.hes-loading-grid { display: grid; grid-template-columns: 1.1fr .9fr; gap: 22px; }
.hes-loading-feature { min-height: 270px; }
.hes-loading-list { display: flex; flex-direction: column; gap: 12px; }
.hes-loading-list span { flex: 1; min-height: 76px; }
@keyframes hesLoadingShimmer { to { transform: translateX(100%); } }
@media (max-width: 767px) {
  .hes-loading-section { min-height: 430px; }
  .hes-loading-header { align-items: flex-start; flex-direction: column; }
  .hes-loading-grid { grid-template-columns: 1fr; gap: 14px; }
  .hes-loading-feature { min-height: 200px; }
  .hes-loading-list { display: grid; grid-template-columns: repeat(2, 1fr); }
  .hes-loading-list span:last-child { display: none; }
}
@media (prefers-reduced-motion: reduce) {
  .hes-loading-header span::after, .hes-loading-feature::after, .hes-loading-list span::after { animation: none; }
}

/* ── Section shell ─────────────────────────────────────── */
.hes-section {
  position: relative;
  padding: 80px 0 60px;
  overflow: hidden;
  background: #f5f7f5;
}

/* ── Background blobs ───────────────────────────────────── */
.hes-bg { position: absolute; inset: 0; pointer-events: none; z-index: 0; }
.hes-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.13;
}
.hes-blob-1 {
  width: 520px; height: 520px;
  background: radial-gradient(circle, #198754, #0d5c38);
  top: -120px; left: -140px;
}
.hes-blob-2 {
  width: 400px; height: 400px;
  background: radial-gradient(circle, #1c7041, #0d3d24);
  bottom: -100px; right: -100px;
}

.hes-container { position: relative; z-index: 1; }

/* ── Header ─────────────────────────────────────────────── */
.hes-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 40px;
}
.hes-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 3px;
  color: #198754;
  margin-bottom: 10px;
}
.hes-eyebrow-dot {
  display: inline-block;
  width: 8px; height: 8px;
  border-radius: 50%;
  background: #198754;
  box-shadow: 0 0 0 3px rgba(25,135,84,.2);
}
.hes-heading {
  font-size: 32px;
  font-weight: 800;
  color: #0f1f16;
  margin: 0;
  line-height: 1.15;
  font-family: 'Raleway', 'Cairo', sans-serif;
  letter-spacing: -0.5px;
}
.hes-viewall-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 26px;
  background: #198754;
  color: #fff;
  border-radius: 50px;
  font-weight: 600;
  font-size: 13.5px;
  text-decoration: none;
  transition: all .3s ease;
  box-shadow: 0 4px 18px rgba(25,135,84,.28);
  white-space: nowrap;
}
.hes-viewall-btn:hover {
  background: #14713f;
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(25,135,84,.38);
}

/* ── Main grid ──────────────────────────────────────────── */
.hes-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: stretch;
}

/* ══════════════ FEATURED CARD ═══════════════════════════ */
.hes-featured-wrap { display: flex; }
.hes-featured-card {
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,.08);
  display: flex;
  flex-direction: column;
  width: 100%;
  transition: box-shadow .3s ease;
}
.hes-featured-card:hover {
  box-shadow: 0 16px 48px rgba(0,0,0,.13);
}

/* Image */
.hes-feat-img-wrap {
  position: relative;
  height: 260px;
  overflow: hidden;
  background: linear-gradient(140deg, #155632 0%, #2f9d63 50%, #198754 100%);
}
.hes-feat-img {
  width: 100%; height: 100%;
  object-fit: cover;
  transition: transform .5s ease;
}
.hes-featured-card:hover .hes-feat-img { transform: scale(1.04); }
.hes-feat-img-ph {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  color: rgba(255,255,255,.35);
  font-size: 72px;
}
.hes-feat-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(to top,
    rgba(0,0,0,.82) 0%,
    rgba(0,0,0,.30) 50%,
    rgba(0,0,0,.05) 100%);
}

/* Badges */
.hes-feat-badges {
  position: absolute; top: 14px; left: 14px; right: 14px;
  display: flex; justify-content: space-between; gap: 8px;
  z-index: 3; flex-wrap: wrap;
}
.hes-badge {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 12px;
  border-radius: 50px;
  font-size: 11.5px; font-weight: 700;
  letter-spacing: .4px;
}
.hes-badge i { font-size: 11px; }
.hes-badge-live {
  background: rgba(220,53,69,.9);
  color: #fff;
  backdrop-filter: blur(6px);
}
.hes-badge-upcoming {
  background: rgba(25,135,84,.9);
  color: #fff;
  backdrop-filter: blur(6px);
}
.hes-badge-paid {
  background: rgba(255,193,7,.9);
  color: #1a1a1a;
  backdrop-filter: blur(6px);
}
.hes-badge-free {
  background: rgba(255,255,255,.18);
  color: #fff;
  border: 1px solid rgba(255,255,255,.4);
  backdrop-filter: blur(6px);
}

/* Live pulse */
.hes-live-pulse {
  display: inline-block;
  width: 7px; height: 7px;
  border-radius: 50%;
  background: #fff;
  animation: hesPulse 1.2s ease-in-out infinite;
}
@keyframes hesPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,.6); }
  50%       { box-shadow: 0 0 0 5px rgba(255,255,255,0); }
}

/* Title inside image */
.hes-feat-title-wrap {
  position: absolute; bottom: 0; left: 0; right: 0;
  padding: 20px 20px 16px;
  z-index: 3;
}
.hes-feat-title {
  font-size: 19px; font-weight: 800;
  color: #fff; margin: 0 0 8px;
  line-height: 1.3;
  text-shadow: 0 1px 8px rgba(0,0,0,.5);
  font-family: 'Raleway', 'Cairo', sans-serif;
}
.hes-feat-meta {
  display: flex; flex-wrap: wrap; gap: 10px;
}
.hes-feat-meta span {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 12px; color: rgba(255,255,255,.88);
}
.hes-feat-meta i { font-size: 11px; color: #7ed8a8; }

/* Bottom panel */
.hes-feat-bottom {
  padding: 20px 22px 22px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  flex: 1;
  background: #fff;
}
.hes-countdown-label {
  display: flex; align-items: center; gap: 7px;
  font-size: 11.5px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 1.5px;
  color: #5a7265;
}
.hes-countdown-label i { color: #198754; font-size: 13px; }

/* Register button */
.hes-register-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 12px 28px;
  background: linear-gradient(135deg, #198754 0%, #22a76a 100%);
  color: #fff;
  border-radius: 50px;
  font-weight: 700; font-size: 13.5px;
  text-decoration: none;
  transition: all .3s ease;
  box-shadow: 0 4px 16px rgba(25,135,84,.30);
  width: 100%; justify-content: center;
}
.hes-register-btn:hover {
  background: linear-gradient(135deg, #14713f 0%, #198754 100%);
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(25,135,84,.42);
}
.hes-register-btn-sm { width: auto; padding: 10px 22px; font-size: 13px; }
.hes-btn-arrow { font-size: 14px; }

/* ══════════════ SIDE LIST ═══════════════════════════════ */
.hes-side { display: flex; flex-direction: column; }
.hes-list { display: flex; flex-direction: column; gap: 0; height: 100%; }

/* Event row */
.hes-event-row {
  display: flex; align-items: center; gap: 14px;
  padding: 16px 16px;
  background: #fff;
  border-radius: 14px;
  margin-bottom: 10px;
  text-decoration: none;
  transition: all .3s ease;
  border: 1.5px solid rgba(25,135,84,.06);
  position: relative;
  overflow: hidden;
}
.hes-event-row::before {
  content: '';
  position: absolute; left: 0; top: 0; bottom: 0;
  width: 3px;
  background: linear-gradient(to bottom, #198754, #22a76a);
  opacity: 0;
  transition: opacity .3s;
}
.hes-event-row:hover {
  transform: translateX(4px);
  box-shadow: 0 6px 22px rgba(25,135,84,.11);
  border-color: rgba(25,135,84,.18);
}
.hes-event-row:hover::before { opacity: 1; }
.hes-rtl .hes-event-row:hover { transform: translateX(-4px); }
.hes-rtl .hes-event-row::before { left: auto; right: 0; }

/* Row number */
.hes-row-num {
  font-size: 22px; font-weight: 900;
  color: rgba(25,135,84,.12);
  font-family: 'Raleway', sans-serif;
  min-width: 32px; text-align: center;
  flex-shrink: 0;
  transition: color .3s;
  line-height: 1;
}
.hes-event-row:hover .hes-row-num { color: rgba(25,135,84,.30); }

/* Thumbnail */
.hes-row-thumb {
  width: 80px; height: 66px;
  border-radius: 10px; overflow: hidden; flex-shrink: 0;
  background: linear-gradient(140deg, #e8f5ec, #f4f9f6);
}
.hes-row-thumb img { width: 100%; height: 100%; object-fit: cover; }
.hes-row-thumb-ph {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  color: #9dcbb0; font-size: 26px;
}

/* Row content */
.hes-row-content { flex: 1; min-width: 0; }
.hes-row-title {
  font-size: 14px; font-weight: 700;
  color: #111c14; margin: 0 0 5px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  font-family: 'Raleway', 'Cairo', sans-serif;
}
.hes-row-info {
  display: flex; flex-wrap: wrap; gap: 8px;
  margin-bottom: 7px;
}
.hes-row-info span {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11.5px; color: #6a7d73;
}
.hes-row-info i { font-size: 11px; color: #198754; }

/* CTA arrow */
.hes-row-cta {
  width: 32px; height: 32px;
  border-radius: 50%;
  background: #f0f7f3;
  display: flex; align-items: center; justify-content: center;
  color: #198754; font-size: 14px;
  flex-shrink: 0;
  transition: all .3s;
}
.hes-event-row:hover .hes-row-cta {
  background: #198754;
  color: #fff;
}

/* See-all footer row */
.hes-seeall-row {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 13px 18px;
  background: linear-gradient(135deg, #f0f7f3 0%, #e8f5ec 100%);
  border-radius: 14px;
  border: 1.5px dashed rgba(25,135,84,.25);
  text-decoration: none;
  font-size: 13px; font-weight: 600;
  color: #198754;
  transition: all .3s;
  margin-top: 4px;
}
.hes-seeall-row:hover {
  background: #198754;
  color: #fff;
  border-color: #198754;
}

/* Empty state */
.hes-empty {
  background: #fff;
  border-radius: 18px;
  padding: 40px 24px;
  text-align: center;
  border: 1.5px dashed rgba(25,135,84,.2);
  display: flex; flex-direction: column;
  align-items: center; gap: 14px;
  height: 100%; justify-content: center;
}
.hes-empty-icon {
  width: 64px; height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e8f5ec, #f0f7f3);
  display: flex; align-items: center; justify-content: center;
  font-size: 28px; color: #198754;
}
.hes-empty p { font-size: 14px; color: #6a7d73; margin: 0; }

/* ══════════════ COUNTDOWN OVERRIDES ════════════════════ */
.hes-section .evt-countdown {
  display: flex; align-items: center; gap: 8px;
}
.hes-section .evt-countdown-lg {
  gap: 12px;
}
.hes-section .evt-cd-unit {
  display: flex; flex-direction: column; align-items: center;
}
.hes-section .evt-countdown-lg .evt-cd-value {
  font-size: 32px; font-weight: 800;
  color: #0f1f16;
  font-family: 'Raleway', sans-serif;
  line-height: 1;
  min-width: 52px; text-align: center;
  background: #f0f7f3;
  border-radius: 10px;
  padding: 8px 6px 6px;
  border-bottom: 3px solid #198754;
}
.hes-section .evt-countdown-lg .evt-cd-label {
  font-size: 10px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 1px;
  color: #7a9282; margin-top: 5px;
}
.hes-section .evt-countdown-lg .evt-cd-sep {
  font-size: 26px; font-weight: 800;
  color: #198754; margin-bottom: 14px;
}
.hes-section .evt-countdown-sm .evt-cd-value {
  font-size: 14px; font-weight: 800;
  color: #198754;
  font-family: 'Raleway', sans-serif;
  background: #f0f7f3;
  border-radius: 6px;
  padding: 3px 7px;
  min-width: 30px; text-align: center;
}
.hes-section .evt-countdown-sm .evt-cd-label {
  font-size: 9px; font-weight: 600;
  text-transform: uppercase; letter-spacing: .8px;
  color: #9ab3a3; margin-top: 3px;
}
.hes-section .evt-countdown-sm .evt-cd-sep {
  font-size: 13px; font-weight: 700;
  color: #c4d8cb; margin-bottom: 12px;
}
.hes-section .evt-countdown-live,
.hes-section .evt-countdown-ended {
  font-size: 13px; font-weight: 700;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 14px;
  border-radius: 50px;
}
.hes-section .evt-countdown-live {
  background: rgba(220,53,69,.1);
  color: #dc3545;
}
.hes-section .evt-countdown-ended {
  background: rgba(108,117,125,.1);
  color: #6c757d;
}
.hes-section .evt-live-dot {
  display: inline-block;
  width: 7px; height: 7px;
  border-radius: 50%; background: #dc3545;
  animation: hesPulse 1.2s infinite;
}

/* ══════════════ RTL ═══════════════════════════════════ */
.hes-rtl { direction: rtl; text-align: right; }
.hes-rtl .hes-header { flex-direction: row-reverse; }
.hes-rtl .hes-heading { font-family: 'Cairo', 'Noto Sans Arabic', sans-serif; }
.hes-rtl .hes-eyebrow { flex-direction: row-reverse; }
.hes-rtl .hes-viewall-btn { flex-direction: row-reverse; }
.hes-rtl .hes-feat-meta { flex-direction: row-reverse; }
.hes-rtl .hes-feat-title-wrap { text-align: right; }
.hes-rtl .hes-row-title { font-family: 'Cairo', 'Noto Sans Arabic', sans-serif; }
.hes-rtl .hes-row-info { flex-direction: row-reverse; }
.hes-rtl .hes-feat-badges { flex-direction: row-reverse; }

/* ══════════════ RESPONSIVE ════════════════════════════ */
@media (max-width: 991px) {
  .hes-grid { grid-template-columns: 1fr; gap: 20px; }
  .hes-feat-img-wrap { height: 240px; }
  .hes-heading { font-size: 26px; }
}
@media (max-width: 767px) {
  .hes-section { padding: 55px 0 40px; }
  .hes-heading { font-size: 22px; }
  .hes-header { flex-direction: column; align-items: flex-start; }
  .hes-rtl .hes-header { align-items: flex-end; }
  .hes-feat-img-wrap { height: 200px; }
  .hes-section .evt-countdown-lg .evt-cd-value { font-size: 24px; min-width: 42px; }
}
@media (max-width: 480px) {
  .hes-blob-1 { width: 300px; height: 300px; }
  .hes-blob-2 { width: 240px; height: 240px; }
  .hes-row-num { display: none; }
  .hes-section .evt-countdown-lg .evt-cd-value { font-size: 20px; min-width: 36px; }
}
`;